/** Silence detection for spoken rapid-fire answers (spec 02). Local and pure. See ADR-0002 §1. */

export interface SilenceState {
  silentMs: number;
  /** True on the single frame the advance should fire. Never true twice for one silence. */
  shouldAdvance: boolean;
  /** Heard speaking yet this turn — gates the silence counter. See ADR-0002 §1. */
  hasSpoken: boolean;
}

export interface SilenceOptions {
  threshold: number;
  advanceAfterMs: number;
}

export function initialSilenceState(): SilenceState {
  return { silentMs: 0, shouldAdvance: false, hasSpoken: false };
}

export function nextSilenceState(
  prev: SilenceState,
  level: number,
  deltaMs: number,
  opts: SilenceOptions,
): SilenceState {
  if (level >= opts.threshold) {
    return { silentMs: 0, shouldAdvance: false, hasSpoken: true };
  }
  // Quiet before the first word is the room, not an answer. See ADR-0002 §1.
  if (!prev.hasSpoken) {
    return { silentMs: 0, shouldAdvance: false, hasSpoken: false };
  }
  const silentMs = prev.silentMs + deltaMs;
  // Fire exactly once: only on the frame that crosses the line.
  const shouldAdvance = prev.silentMs < opts.advanceAfterMs && silentMs >= opts.advanceAfterMs;
  return { silentMs, shouldAdvance, hasSpoken: true };
}

/** Mean normalised amplitude of an analyser frame, [0, 1]. Not `computeAudioLevels` — see ADR-0002 §1. */
export function frameLevel(dataArray: Uint8Array): number {
  if (dataArray.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
  return sum / dataArray.length / 255;
}
